/**
 * Cadastros → de quem é cada código:
 * - Vendedores sem cadastro: códigos do Winthor e nomes do SAP que apareceram
 *   nas vendas do mês e ainda não estão ligados a ninguém (ligar aqui corrige o
 *   painel na hora).
 * - Códigos do Winthor e nomes no SAP: de quem é cada um, e desde quando. Quando
 *   um representante sai e outro assume os clientes, o código passa para o novo
 *   "a partir de" um mês (o jeito mais fácil: "Saiu da equipe", em Representantes).
 *
 * Lê: os cadastros e as vendas do mês (para achar códigos sem cadastro).
 * Usado por: telas/Cadastros.tsx.
 */
import { useMemo, useState } from 'react';
import { ErroNaTela } from '../../componentes/basicos.tsx';
import { api, ErroApi } from '../../dados/api.ts';
import { usarDadosDoMes } from '../../dados/usar-dados-do-mes.ts';
import { mesesDepois } from '../../dominio/datas.ts';
import { juntarFeriados } from '../../dominio/feriados.ts';
import { linhaDoTempoDosCodigos, type PeriodoDaLigacao } from '../../dominio/identificacao.ts';
import { montarPainel, type VendedorNaoIdentificado } from '../../dominio/painel.ts';
import type { Cadastros as TipoCadastros, Fonte, Mes } from '../../dominio/tipos.ts';
import { formatarMoeda, NOME_DO_SISTEMA, nomeDoMes, nomeProprio } from '../../formatacao.ts';
import { descreverPeriodo, porNome, type Executar } from './comum.ts';

export function SemCadastro({ mes, cadastros, executar }: { mes: Mes; cadastros: TipoCadastros; executar: Executar }) {
  const estadoWinthor = usarDadosDoMes(mes, 'winthor');
  const estadoSap = usarDadosDoMes(mes, 'sap-bauny');
  const winthor = estadoWinthor.dados;
  const sap = estadoSap.dados;
  const erroAoCarregar = estadoWinthor.erro ?? estadoSap.erro;
  const [escolhas, setEscolhas] = useState<Record<string, string>>({});
  // Os dois sistemas: códigos do Winthor e nomes do SAP sem dono no mês.
  const naoIdentificados = useMemo(() => {
    const lista: VendedorNaoIdentificado[] = [];
    for (const [fonte, dados] of [['winthor', winthor], ['sap-bauny', sap]] as const) {
      if (dados) lista.push(...montarPainel({ mes: dados.mes, fonte, vendas: dados.vendas, cadastros, metas: dados.metas, feriados: juntarFeriados(cadastros.feriados) }).naoIdentificados);
    }
    return lista;
  }, [winthor, sap, cadastros]);
  const carregado = winthor !== null && sap !== null;
  const opcoes = [...cadastros.representantes].sort(porNome);

  return (
    <section className="cartao pilha">
      <div>
        <h2>Vendedores sem cadastro em {nomeDoMes(mes)}</h2>
        <p className="subtitulo">
          Códigos do Winthor e nomes do SAP que venderam no mês e não estão ligados a um representante. As vendas deles ficam fora da conta até a ligação.
          Ligar aqui vale para todos os meses; para passar um código de uma pessoa para outra, use as listas de códigos abaixo.
        </p>
      </div>
      {erroAoCarregar ? (
        <ErroNaTela erro={new ErroApi(erroAoCarregar.codigo, `Não deu para buscar as vendas do mês: ${erroAoCarregar.message} Recarregue a página (F5).`, erroAoCarregar.status, erroAoCarregar.pedido)} />
      ) : null}
      {naoIdentificados.length === 0 ? (
        erroAoCarregar ? null : <p className="texto-apagado">{carregado ? 'Nenhum: todos os vendedores do mês estão cadastrados.' : 'Carregando…'}</p>
      ) : (
        <div className="tabela-rolavel">
          <table className="tabela">
            <thead>
              <tr>
                <th>Sistema</th>
                <th>Código</th>
                <th>Nome no sistema</th>
                <th className="numero">Notas</th>
                <th className="numero">Líquido</th>
                <th>Ligar a</th>
              </tr>
            </thead>
            <tbody>
              {naoIdentificados.map((vendedor) => {
                const chave = `${vendedor.fonte}|${vendedor.codigo}`;
                const escolhido = escolhas[chave] ?? '';
                return (
                  <tr key={chave}>
                    <td>{NOME_DO_SISTEMA[vendedor.fonte]}</td>
                    <td>{vendedor.codigo}</td>
                    <td>{nomeProprio(vendedor.nome)}</td>
                    <td className="numero">{vendedor.notas}</td>
                    <td className="numero">{formatarMoeda(vendedor.liquido)}</td>
                    <td>
                      <div className="linha-campos">
                        <select aria-label={`Representante do código ${vendedor.codigo}`} value={escolhido} onChange={(e) => setEscolhas((atual) => ({ ...atual, [chave]: e.target.value }))}>
                          <option value="">Escolha…</option>
                          {opcoes.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.nome}
                              {r.ativo ? '' : ' (inativo)'}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="botao"
                          disabled={escolhido === ''}
                          onClick={() =>
                            void executar(
                              () => api.vincularCodigo(vendedor.fonte, vendedor.codigo, Number(escolhido), null),
                              `Código ${vendedor.codigo} ligado a ${opcoes.find((r) => r.id === Number(escolhido))?.nome ?? ''}.`,
                            )
                          }
                        >
                          Ligar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Como cada sistema chama o vendedor, para os textos da seção. */
const TEXTOS_DA_FONTE: Record<Fonte, { titulo: string; campo: string; exemplo: string; largura: number }> = {
  winthor: { titulo: 'Códigos do Winthor', campo: 'Código', exemplo: 'ex.: 42', largura: 96 },
  'sap-bauny': { titulo: 'Nomes no SAP (Bauny)', campo: 'Nome no SAP', exemplo: 'ex.: GO - MEIRELES - Nome Sobrenome', largura: 300 },
};

export function CodigosDaFonte({ fonte, mes, cadastros, executar }: { fonte: Fonte; mes: Mes; cadastros: TipoCadastros; executar: Executar }) {
  const textos = TEXTOS_DA_FONTE[fonte];
  const [codigo, setCodigo] = useState('');
  const [representanteId, setRepresentanteId] = useState('');
  /** '' = desde sempre; senão, o mês AAAA-MM. */
  const [desde, setDesde] = useState('');
  const linhas = linhaDoTempoDosCodigos(cadastros.codigos, fonte);
  const opcoes = [...cadastros.representantes].sort(porNome);
  const nomeDe = (id: number) => cadastros.representantes.find((r) => r.id === id)?.nome ?? `#${id}`;
  // "A partir de": de 12 meses antes a 3 meses depois do mês desta tela.
  const meses = Array.from({ length: 16 }, (_, i) => mesesDepois(mes, 3 - i));

  async function ligar() {
    const id = Number(representanteId);
    const quando = desde === '' ? 'desde sempre' : `a partir de ${nomeDoMes(desde)}`;
    const certo = await executar(() => api.vincularCodigo(fonte, codigo.trim(), id, desde === '' ? null : desde), `${textos.campo} ${codigo.trim()} ligado a ${nomeDe(id)} ${quando}.`);
    if (certo) setCodigo('');
  }

  function remover(ligacao: PeriodoDaLigacao) {
    const pergunta = `Tirar "${ligacao.codigo}" de ${nomeDe(ligacao.representanteId)} (${descreverPeriodo(ligacao)})? As vendas desse período passam para a ligação anterior, ou ficam sem cadastro.`;
    if (!window.confirm(pergunta)) return;
    void executar(() => api.desvincularCodigo(fonte, ligacao.codigo, ligacao.desde), `Ligação de "${ligacao.codigo}" removida.`);
  }

  return (
    <section className="cartao pilha">
      <div>
        <h2>{textos.titulo}</h2>
        <p className="subtitulo">
          De quem é cada {fonte === 'winthor' ? 'código' : 'nome'} de vendedor. Quando um representante sai e outro assume os clientes, ligue ao novo a partir do
          mês da troca: os meses anteriores continuam com quem vendia.
        </p>
      </div>
      <div className="linha-campos antes-depois">
        <label>
          {textos.campo}
          <input value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder={textos.exemplo} style={{ width: textos.largura, maxWidth: '100%' }} />
        </label>
        <label>
          Representante
          <select value={representanteId} onChange={(e) => setRepresentanteId(e.target.value)}>
            <option value="">Escolha…</option>
            {opcoes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
                {r.ativo ? '' : ' (inativo)'}
              </option>
            ))}
          </select>
        </label>
        <label>
          A partir de
          <select value={desde} onChange={(e) => setDesde(e.target.value)}>
            <option value="">sempre (todos os meses)</option>
            {meses.map((m) => (
              <option key={m} value={m}>
                {nomeDoMes(m)}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="botao" onClick={() => void ligar()} disabled={codigo.trim() === '' || representanteId === ''}>
          Ligar
        </button>
      </div>
      <div className="tabela-rolavel">
        <table className="tabela">
          <thead>
            <tr>
              <th>{textos.campo}</th>
              <th>De quem é</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha) => (
              <tr key={linha.codigo}>
                <td>{linha.codigo}</td>
                <td>
                  <ul className="lista-ligacoes">
                    {linha.ligacoes.map((ligacao) => (
                      <li key={ligacao.desde ?? 'sempre'}>
                        <strong>{nomeDe(ligacao.representanteId)}</strong> <span className="texto-apagado">{descreverPeriodo(ligacao)}</span>{' '}
                        <button type="button" className="botao-link" onClick={() => remover(ligacao)}>
                          remover
                        </button>
                      </li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
